import { Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export const STATUS_STYLES = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Lost: "bg-red-50 text-red-700 border-red-200",
  Closed: "bg-gray-100 text-gray-600 border-gray-200",
  Prospective: "bg-amber-50 text-amber-700 border-amber-200",
  Inactive: "bg-gray-100 text-gray-600 border-gray-200",
  Scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-red-50 text-red-600 border-red-200",
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-gray-100 text-gray-600 border-gray-200",
  new: "bg-gold-100 text-gold-700 border-gold",
  responded: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
};

export function StatusBadge({ status }) {
  return (
    <span data-testid={`status-badge-${String(status).toLowerCase()}`}
      className={`inline-flex items-center px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider border rounded-sm ${STATUS_STYLES[status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {status}
    </span>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = "Delete", onConfirm, loading }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-xl">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn" disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-action-btn" onClick={onConfirm} disabled={loading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center py-16 px-6 text-center border border-dashed border-border bg-white/50">
      {Icon && (
        <div className="h-12 w-12 flex items-center justify-center bg-gold-100 mb-4">
          <Icon className="h-6 w-6 text-gold-700" />
        </div>
      )}
      <h3 className="font-serif text-xl font-semibold text-navy">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function PageHeader({ title, description, children, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8" data-testid={testId}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight">{title}</h1>
        {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}

export function PaginationBar({ page, pages, total, onChange, testId = "pagination" }) {
  if (pages <= 1) return null;
  const nums = [];
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || Math.abs(i - page) <= 1) nums.push(i);
  }
  const deduped = [...new Set(nums)];
  return (
    <div className="flex items-center justify-between mt-6" data-testid={testId}>
      <p className="text-xs text-muted-foreground">{total} record{total === 1 ? "" : "s"}</p>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" data-testid="pagination-prev" disabled={page <= 1} onClick={() => onChange(page - 1)}>Prev</Button>
        {deduped.map((n, idx) => (
          <span key={n} className="flex items-center">
            {idx > 0 && deduped[idx - 1] !== n - 1 && <span className="px-1 text-muted-foreground">…</span>}
            <button data-testid={`pagination-page-${n}`} onClick={() => onChange(n)}
              className={`h-8 w-8 text-sm font-medium transition-colors ${n === page ? "bg-navy text-ivory" : "hover:bg-muted text-foreground"}`}>
              {n}
            </button>
          </span>
        ))}
        <Button variant="outline" size="sm" data-testid="pagination-next" disabled={page >= pages} onClick={() => onChange(page + 1)}>Next</Button>
      </div>
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground" data-testid="loading-indicator">
      <Loader2 className="h-5 w-5 animate-spin text-gold" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function Field({ label, error, children, htmlFor }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-wider text-navy-700">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600" data-testid={`${htmlFor}-error`}>{error}</p>}
    </div>
  );
}
